import { Typography, Box, CircularProgress } from "@mui/material";
import Link from "@/components/link";
import { useParams } from "react-router-dom";
import { useAuthContext } from "@/context/AuthContext";
import { useDocument } from "react-firebase-hooks/firestore";
import { doc, collection } from "firebase/firestore";
import { firestore } from "@/firebase";
import { cartConverter } from "@/converters";
import { CartStatus } from "@t3/shared";
import { CartBalanceSheet } from "./CartBalanceSheet";
import { CartHistoryTangoItem } from "./CartHistoryTangoItem";
import { CartHistoryTravelItem } from "./CartHistoryTravelItem";
import { CartHistoryZincItem } from "./CartHistoryZincItem";

export const ShopHistoryDetail = () => {
  const { historyId } = useParams();
  const { user } = useAuthContext();

  // Subscribe to cart history document
  const cartRef = doc(
    collection(
      firestore,
      `company/${user.company}/users/${user.id}/cart`,
    ).withConverter(cartConverter),
    historyId!,
  );

  const [cartSnapshot, cartLoading, cartError] = useDocument(cartRef);

  const cartData = cartSnapshot?.data() || null;

  if (cartLoading || cartSnapshot?.data()?.status !== "purchased") {
    if (cartSnapshot?.data()?.status === "failed") {
      return (
        <Box>
          <Typography variant="body1">Order failed</Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Processing your order...
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  if (!cartData || cartError) {
    return <div>Error loading order history</div>;
  }

  if (!cartData.purchaseResults) {
    throw new Error("No purchase results");
  }

  return (
    <div data-cypress="history-detail-container">
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{ mb: 2 }}
        data-cypress="history-detail-title"
      >
        <CartTitle status={cartData.status} />
      </Typography>

      {cartData.purchaseResults.tangoPurchaseResults &&
        Object.entries(cartData.purchaseResults.tangoPurchaseResults).map(
          ([utid, products]) =>
            products.map((product, index) => (
              <CartHistoryTangoItem key={utid + index} product={product} />
            )),
        )}

      {cartData.purchaseResults.travelPurchaseResults &&
        Object.entries(cartData.purchaseResults.travelPurchaseResults).map(
          ([utid, products]) =>
            products.map((product, index) => (
              <CartHistoryTravelItem key={utid + index} product={product} />
            )),
        )}

      {cartData.purchaseResults.zincPurchaseResults &&
        Object.entries(cartData.purchaseResults.zincPurchaseResults).map(
          ([utid, results]) => (
            <CartHistoryZincItem key={utid} results={results} />
          ),
        )}

      <CartBalanceSheet cartData={cartData} />
      <Typography
        variant="body2"
        sx={{ mt: 2, mb: 3, color: "#555" }}
        data-cypress="delivery-info-text"
      >
        {`Delivery rewards are processed instantly but may take up to one day to be delivered. If you
        don't see your confirmation email, please check both your inbox and spam/junkmail.`}
      </Typography>
      <Typography variant="subtitle1" align="center">
        <Link to="/shop" data-cypress="back-to-shop-button">
          Back to Shop
        </Link>
      </Typography>
    </div>
  );
};

const CartTitle = ({ status }: { status: CartStatus }) => {
  switch (status) {
    case "submitted":
      return "We have received your order!";
    case "purchased":
      return "We have received your order!";
    case "failed":
      return (
        <div>
          <div>This order has been processed...</div>
          <div style={{ fontSize: 18, color: "red" }}>
            However, one or more items encountered an error. Items that were not
            delivered were not charged to you, and you can choose to make
            another order.
          </div>
        </div>
      );
    default:
      return "Unknown status";
  }
};
